import { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  useBankTransferConfigSummary,
  useRevealBankTransferConfig,
  useSaveBankTransferConfig,
} from '../../hooks/useBankTransferConfig';
import type { BankTransferConfigDetail } from '../../types/bankTransferConfig';

// ── Zod schema ──────────────────────────────────────────────────────────────

const timingPolicySchema = z.object({
  ownerReviewHours: z.coerce.number().int().min(1).max(168).optional(),
  initialTransferDays: z.coerce.number().int().min(1).max(60).optional(),
  settlementGraceDays: z.coerce.number().int().min(1).max(30).optional(),
  reminderOffsetsHours: z.array(z.object({ value: z.coerce.number().int().min(1) })),
  balanceDueDaysBeforeCheckIn: z.coerce.number().int().min(0).max(365).optional(),
  maxExtensionDays: z.coerce.number().int().min(0).max(60).optional(),
  safeCutoffHoursBeforeCheckIn: z.coerce.number().int().min(1).max(720).optional(),
});

const bankTransferConfigSchema = z.object({
  accountHolder: z.string().min(1).max(200),
  iban: z.string().min(1).max(80),
  bic: z.string().max(11).optional(),
  bankName: z.string().max(200).optional(),
  currency: z.string().regex(/^[A-Z]{3}$/, 'Must be a 3-letter ISO 4217 currency code'),
  guestInstructions: z.string().min(1).max(2000),
  referenceGuidance: z.string().min(1).max(500),
  policy: timingPolicySchema,
  enabled: z.boolean(),
});

type FormData = z.infer<typeof bankTransferConfigSchema>;

// ── helpers ─────────────────────────────────────────────────────────────────

const isAxiosStatus = (err: unknown, status: number): boolean =>
  (err as { response?: { status?: number } })?.response?.status === status;

const detailToForm = (detail: BankTransferConfigDetail): FormData => ({
  accountHolder: detail.accountHolder,
  iban: detail.iban,
  bic: detail.bic ?? undefined,
  bankName: detail.bankName ?? undefined,
  currency: detail.currency,
  guestInstructions: detail.guestInstructions,
  referenceGuidance: detail.referenceGuidance,
  policy: {
    ownerReviewHours: detail.policy?.ownerReviewHours ?? undefined,
    initialTransferDays: detail.policy?.initialTransferDays ?? undefined,
    settlementGraceDays: detail.policy?.settlementGraceDays ?? undefined,
    reminderOffsetsHours: (detail.policy?.reminderOffsetsHours ?? []).map((v) => ({ value: v })),
    balanceDueDaysBeforeCheckIn: detail.policy?.balanceDueDaysBeforeCheckIn ?? undefined,
    maxExtensionDays: detail.policy?.maxExtensionDays ?? undefined,
    safeCutoffHoursBeforeCheckIn: detail.policy?.safeCutoffHoursBeforeCheckIn ?? undefined,
  },
  enabled: detail.enabled,
});

// ── Props ───────────────────────────────────────────────────────────────────

interface BankTransferConfigSectionProps {
  readonly apartmentId: string;
}

// ── Component ───────────────────────────────────────────────────────────────

export const BankTransferConfigSection = ({ apartmentId }: BankTransferConfigSectionProps) => {
  const { t } = useTranslation(['forms', 'common', 'errors']);
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [expectedVersion, setExpectedVersion] = useState<number | null>(null);

  const summaryQuery = useBankTransferConfigSummary(apartmentId);
  const revealQuery = useRevealBankTransferConfig(apartmentId);
  const saveMutation = useSaveBankTransferConfig(apartmentId);

  const isEditing = idempotencyKey !== null && revealQuery.data !== undefined;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(bankTransferConfigSchema),
    defaultValues: {
      policy: { reminderOffsetsHours: [] },
      enabled: true,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'policy.reminderOffsetsHours' });

  // Pre-populate form when reveal data arrives
  useEffect(() => {
    if (revealQuery.data !== undefined) {
      if (revealQuery.data === null) {
        // 404 → create mode
        reset({ policy: { reminderOffsetsHours: [] }, enabled: true });
        setExpectedVersion(null);
      } else {
        reset(detailToForm(revealQuery.data));
        setExpectedVersion(revealQuery.data.version);
      }
    }
  }, [revealQuery.data, reset]);

  const handleEditClick = async () => {
    setIdempotencyKey(crypto.randomUUID());
    await revealQuery.refetch();
  };

  const handleCancel = () => {
    setIdempotencyKey(null);
    saveMutation.reset();
  };

  const onSubmit = async (data: FormData) => {
    if (!idempotencyKey) return;
    try {
      await saveMutation.mutateAsync({
        request: {
          expectedVersion,
          accountHolder: data.accountHolder,
          iban: data.iban,
          bic: data.bic,
          bankName: data.bankName,
          currency: data.currency,
          guestInstructions: data.guestInstructions,
          referenceGuidance: data.referenceGuidance,
          policy: {
            ownerReviewHours: data.policy.ownerReviewHours,
            initialTransferDays: data.policy.initialTransferDays,
            settlementGraceDays: data.policy.settlementGraceDays,
            reminderOffsetsHours: data.policy.reminderOffsetsHours.map((r) => r.value),
            balanceDueDaysBeforeCheckIn: data.policy.balanceDueDaysBeforeCheckIn,
            maxExtensionDays: data.policy.maxExtensionDays,
            safeCutoffHoursBeforeCheckIn: data.policy.safeCutoffHoursBeforeCheckIn,
          },
          enabled: data.enabled,
        },
        idempotencyKey,
        version: expectedVersion,
      });
      setIdempotencyKey(null);
    } catch {
      // 409 handled via saveMutation.error below
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────

  if (summaryQuery.isPending) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div role="status" className="animate-pulse space-y-3">
          <div className="h-5 bg-gray-200 rounded w-1/3" />
          <div className="h-4 bg-gray-100 rounded w-1/2" />
          <div className="h-4 bg-gray-100 rounded w-2/3" />
        </div>
      </div>
    );
  }

  // ── Not authorised (403) ─────────────────────────────────────────────────

  if (summaryQuery.isError && isAxiosStatus(summaryQuery.error, 403)) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('forms:bankTransferConfig.heading')}</h2>
        <p className="text-sm text-gray-500">{t('forms:bankTransferConfig.notAuthorised')}</p>
      </div>
    );
  }

  // ── Not configured (404) — empty state ──────────────────────────────────

  const isNotConfigured = summaryQuery.isError && isAxiosStatus(summaryQuery.error, 404);

  if (isNotConfigured && !isEditing) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('forms:bankTransferConfig.heading')}</h2>
        <p className="text-sm text-gray-500 mb-4">{t('forms:bankTransferConfig.emptyState')}</p>
        <button
          onClick={handleEditClick}
          className="py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
        >
          {t('forms:bankTransferConfig.setupButton')}
        </button>
      </div>
    );
  }

  const config = summaryQuery.data;

  // ── Editing state ────────────────────────────────────────────────────────

  if (isEditing) {
    const is409 = saveMutation.isError && isAxiosStatus(saveMutation.error, 409);

    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('forms:bankTransferConfig.heading')}</h2>

        {is409 && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between gap-3">
            <p className="text-sm text-yellow-800">{t('forms:bankTransferConfig.staleVersionBanner')}</p>
            <button
              type="button"
              onClick={async () => {
                const result = await revealQuery.refetch();
                if (result.data) {
                  reset(detailToForm(result.data));
                  setExpectedVersion(result.data.version);
                  setIdempotencyKey(crypto.randomUUID());
                }
                saveMutation.reset();
              }}
              className="text-sm font-medium text-yellow-700 underline whitespace-nowrap"
            >
              {t('forms:bankTransferConfig.reload')}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Account holder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('forms:bankTransferConfig.accountHolder')}
            </label>
            <input
              {...register('accountHolder')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.accountHolder && <p className="text-sm text-red-600 mt-1">{errors.accountHolder.message}</p>}
          </div>

          {/* IBAN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('forms:bankTransferConfig.iban')}
            </label>
            <input
              {...register('iban')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.iban && <p className="text-sm text-red-600 mt-1">{errors.iban.message}</p>}
          </div>

          {/* BIC */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('forms:bankTransferConfig.bic')}
            </label>
            <input
              {...register('bic')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Bank name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('forms:bankTransferConfig.bankName')}
            </label>
            <input
              {...register('bankName')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('forms:bankTransferConfig.currency')}
            </label>
            <input
              {...register('currency')}
              placeholder="EUR"
              maxLength={3}
              className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.currency && <p className="text-sm text-red-600 mt-1">{errors.currency.message}</p>}
          </div>

          {/* Guest instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('forms:bankTransferConfig.guestInstructions')}
            </label>
            <textarea
              {...register('guestInstructions')}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.guestInstructions && <p className="text-sm text-red-600 mt-1">{errors.guestInstructions.message}</p>}
          </div>

          {/* Reference guidance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('forms:bankTransferConfig.referenceGuidance')}
            </label>
            <textarea
              {...register('referenceGuidance')}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.referenceGuidance && <p className="text-sm text-red-600 mt-1">{errors.referenceGuidance.message}</p>}
          </div>

          {/* Enabled toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="enabled"
              {...register('enabled')}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
            />
            <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
              {t('forms:bankTransferConfig.enabled')}
            </label>
          </div>

          {/* Timing policy */}
          <fieldset className="border border-gray-200 rounded-lg p-4">
            <legend className="text-sm font-semibold text-gray-700 px-1">
              {t('forms:bankTransferConfig.timingPolicy')}
            </legend>
            <div className="space-y-4 mt-3">
              {/* Reminder offsets */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('forms:bankTransferConfig.policy.reminderOffsetsHours')}
                </label>
                {fields.length === 0 && (
                  <p className="text-sm text-gray-400 mb-2">{t('forms:bankTransferConfig.noReminders')}</p>
                )}
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-center gap-2 mb-2">
                    <input
                      {...register(`policy.reminderOffsetsHours.${index}.value`)}
                      type="number"
                      min={1}
                      className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={14} />
                      <span className="sr-only">{t('forms:bankTransferConfig.removeReminder')}</span>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => append({ value: 24 })}
                  className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
                >
                  <Plus size={14} />
                  {t('forms:bankTransferConfig.addReminder')}
                </button>
              </div>

              {/* Numeric policy fields */}
              {(
                [
                  ['ownerReviewHours', 'policy.ownerReviewHours'],
                  ['initialTransferDays', 'policy.initialTransferDays'],
                  ['settlementGraceDays', 'policy.settlementGraceDays'],
                  ['balanceDueDaysBeforeCheckIn', 'policy.balanceDueDaysBeforeCheckIn'],
                  ['maxExtensionDays', 'policy.maxExtensionDays'],
                  ['safeCutoffHoursBeforeCheckIn', 'policy.safeCutoffHoursBeforeCheckIn'],
                ] as const
              ).map(([field, labelKey]) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t(`forms:bankTransferConfig.${labelKey}` as const)}
                  </label>
                  <input
                    {...register(`policy.${field}` as const)}
                    type="number"
                    className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              ))}
            </div>
          </fieldset>

          {saveMutation.isError && !is409 && (
            <p className="text-sm text-red-600">{t('errors:somethingWentWrong')}</p>
          )}

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 min-h-[44px]"
            >
              {saveMutation.isPending ? t('forms:bankTransferConfig.saving') : t('forms:bankTransferConfig.save')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm min-h-[44px]"
            >
              {t('forms:bankTransferConfig.cancel')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ── View state ───────────────────────────────────────────────────────────

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{t('forms:bankTransferConfig.heading')}</h2>
        {config && (
          <button
            onClick={handleEditClick}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
            aria-label={t('forms:bankTransferConfig.editButton')}
          >
            <Pencil size={16} />
          </button>
        )}
      </div>

      {config && (
        <div className="space-y-3">
          {!config.enabled && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {t('forms:bankTransferConfig.disabled')}
            </span>
          )}
          <div>
            <p className="text-sm font-medium text-gray-500">{t('forms:bankTransferConfig.maskedIban')}</p>
            <p className="text-gray-900">{config.maskedIban}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">{t('forms:bankTransferConfig.currency')}</p>
            <p className="text-gray-900">{config.currency}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankTransferConfigSection;
